import Navbar from "../components/Navbar"
import KeyFeatures from "../components/KeyFeatures"
import Benefits from "../components/Benefits"
import UseCasesSection from "../components/UseCasesSection"
import FaqSection from "../components/FaqSection"
import GetStartedCard from "../components/GetStartedCard"
import GetInTouch from "../components/GetInTouch"
import Footer from "../components/Footer"
import heroBg from "../assets/hero-bg.png"
import petrolImg from "../assets/petrol.png"
import vehicleImg from "../assets/Vehicle-Tracking.jpg"
import trackSmartImg from "../assets/tracksmart.jpg"
import useCaseLogisticsImg from "../assets/Vehicle-Tracking.jpg"
import useCaseConstructionImg from "../assets/truck2.webp"
import useCaseFieldServicesImg from "../assets/truck3.webp"
import useCaseHealthcareImg from "../assets/truck1.webp"
import homeVehicletrackingImg from "../assets/homeVehicletracking.png"
import scalableImg from "../assets/scalable.webp"

const videoFeatures = [
  {
    title: "Real-Time GPS Tracking",
    desc: "Monitor vehicle locations continuously with precise GPS technology, allowing you to track your entire fleet from a single dashboard.",
  },
  {
    title: "Geofencing",
    desc: "Set virtual boundaries and receive alerts on breaches, helping you maintain control over vehicle movements and prevent unauthorized use.",
  },
  {
    title: "Driver Behavior Analysis",
    desc: "Assess driving patterns for safety improvements by monitoring speeding, harsh braking, and rapid acceleration to enhance driver safety.",
  },
  {
    title: "Maintenance Alerts",
    desc: "Schedule timely vehicle servicing with automated maintenance reminders based on mileage, engine hours, or calendar intervals.",
  },
  {
    title: "Customizable Reports",
    desc: "Generate reports tailored to your business needs, providing valuable insights into fleet performance, utilization, and efficiency.",
  },
  {
    title: "Mobile Access",
    desc: "Manage your fleet from anywhere using our mobile app, giving you the flexibility to monitor operations on the go.",
  },
]

const benefits = [
  {
    number: "01",
    title: "Integrated Technology",
    desc: "Our systems streamline routes, reduce costs, and minimize resource utilization across your fleet operations.",
  },
  {
    number: "02",
    title: "Scalable Solutions",
    desc: "From startups to large logistics networks, we deliver customizable solutions tailored to your specific operational needs.",
  },
  {
    number: "03",
    title: "Field-Tested Reliability",
    desc: "Every product we deploy is field-tested for consistency, uptime, and safety — ensuring seamless results in any environment.",
  },
  {
    number: "04",
    title: "Data-Driven Insights",
    desc: "Transform your fleet data into actionable insights with comprehensive analytics and reporting tools.",
  },
]

const useCases = [
  {
    number: "01",
    title: "GPS & Cellular Connectivity",
    desc: "Reliable connectivity for live location, vehicle status, and fleet visibility.",
    variant: "side",
    image: useCaseLogisticsImg,
  },
  {
    number: "02",
    title: "IoT Sensors",
    desc: "Advanced sensors for monitoring temperature, humidity, shock, fuel, and other critical parameters.",
    variant: "center",
    image: homeVehicletrackingImg,
  },
  {
    number: "03",
    title: "Cloud Infrastructure",
    desc: "Secure cloud infrastructure for dashboards, alerts, reports, analytics, and monitoring.",
    variant: "side",
    image: useCaseFieldServicesImg,
  },
  {
    number: "04",
    title: "Fleet Reporting",
    desc: "Generate reports on utilization, fuel, and routes with intuitive dashboards and automated alerts.",
    variant: "side",
    image: scalableImg,
  },
]

export default function Section9() {
  const containerClass =
    "mx-auto w-full max-w-[1600px] px-5 sm:px-8 lg:px-12 xl:px-20"

  return (
    <>
      {/* Hero */}
      <section className="relative mx-auto w-full max-w-[calc(100%-24px)] overflow-hidden rounded-b-[42px] bg-black sm:max-w-[calc(100%-32px)] lg:max-w-[calc(100%-48px)]">
        <Navbar />

        <div
          className="relative min-h-[560px] bg-cover bg-center sm:min-h-[620px] lg:min-h-[700px]"
          style={{ backgroundImage: `url(${heroBg})` }}
        >
          <div className="absolute inset-0 bg-black/68" />

          <div className="relative z-10 flex min-h-[560px] items-center justify-center px-5 pt-24 text-center sm:min-h-[620px] lg:min-h-[700px]">
            <div className="mx-auto max-w-[920px]">
              <h1 className="text-[34px] font-bold leading-tight text-white sm:text-5xl lg:text-[60px]">
                Advanced Video Telematics <br className="hidden sm:block" />
                Software
              </h1>

              <div className="mx-auto mt-3 h-[3px] w-[240px] rounded-full bg-[#ed860f] sm:w-[420px] lg:w-[520px]" />

              <p className="mx-auto mt-7 max-w-[760px] text-sm leading-relaxed text-white/75 sm:text-base">
                Real-time GPS tracking, driver behavior monitoring, and intelligent video solutions for a more efficient and safer fleet.
              </p>

              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <a
                  href="/services"
                  className="inline-flex min-w-[210px] items-center justify-center rounded-md bg-[#ed860f] px-8 py-4 text-sm font-medium text-white transition hover:bg-[#d9780d]"
                >
                  Explore Solutions
                </a>

                <a
                  href="/contact"
                  className="inline-flex min-w-[210px] items-center justify-center rounded-md border border-white px-8 py-4 text-sm font-medium text-white transition hover:bg-white hover:text-black"
                >
                  Request demo
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-14 sm:py-16 lg:py-20">
        <div className={`${containerClass} grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-16`}>
          <div>
            <h2 className="text-3xl font-bold leading-tight text-black sm:text-4xl lg:text-[42px]">
              Vehicle Tracking Built for Your Business
            </h2>

            <p className="mt-3 text-sm font-semibold leading-relaxed text-gray-600 sm:text-[15px]">
              Our comprehensive solution helps businesses with live vehicle tracking, fleet operations, and data-driven control.
            </p>

            <h3 className="mt-8 text-xl font-bold text-black">
              Tailored Solutions for Every Business
            </h3>

            <p className="mt-4 max-w-[720px] text-sm leading-relaxed text-gray-600 sm:text-[15px]">
              Our system offers robust analytics and reporting tools, allowing you to generate daily, weekly, and fully customized reports. You can monitor real-time vehicle fuel consumption with precision and gain deeper insights into operational efficiency across all records.
            </p>

            <p className="mt-4 max-w-[720px] text-sm leading-relaxed text-gray-600 sm:text-[15px]">
              Real-time alerts notify you instantly in cases of fuel theft, unauthorized disconnections, route deviation, harsh stops, poor eco-based access, and mobile web compatibility, managers can track operations remotely, while smart reports and a complete audit trail ensure full transparency and accountability.
            </p>

            <a
              href="/services"
              className="mt-7 inline-flex items-center justify-center rounded-md bg-[#ed860f] px-8 py-3 text-sm font-medium text-white transition hover:bg-[#d9780d]"
            >
              Discover Features
            </a>
          </div>

          <div className="overflow-hidden rounded-xl lg:justify-self-end">
            <img
              src={vehicleImg}
              alt="Vehicle tracking solution"
              className="h-[270px] w-full object-cover sm:h-[360px] lg:h-[430px] lg:max-w-[620px]"
            />
          </div>
        </div>
      </section>

      <KeyFeatures features={videoFeatures} />

      <Benefits items={benefits} image={trackSmartImg} imageAlt="TrackSmart vehicle tracking platform" />

      <UseCasesSection items={useCases} />

      <FaqSection />

      <section className="bg-white py-10 sm:py-12 lg:py-16">
        <div className={containerClass}>
          <GetStartedCard
            title="Get Started in Minutes"
            desc="Experience the power of automation with FleetRobo® AIS-140 ICAT Certified Devices"
            primaryText="Explore Solutions"
            secondaryText="Contact us"
            primaryHref="/services"
            secondaryHref="/contact"
          />
        </div>
      </section>

      <GetInTouch />
      <Footer />
    </>
  )
}
